import { Account, createRateLimitHandler, RateLimit } from '../../index';
import { MyTripRequest } from '../../generated/account';
import { logger } from '@tuic/logger';
import { ACCESS_TOKEN_RATE_LIMIT_NAME } from './access-token.types';
import { errorContains } from '../../utils/error';
import { Client } from '@hey-api/client-fetch';
import { TFunction } from '@tuic/i18n';

export enum GetAccessTokenStatus {
  SUCCESS,
  RATE_LIMITED,
  NAME_DOES_NOT_MATCH,
  BAD_BOOKING_CODE,
  BOOKING_NOT_FOUND,
  BOOKING_IN_THE_PAST,
  UNEXPECTED_STRING_RESPONSE,
  GENERAL_ERROR,
}

type GetAccessTokenHandler = (
  args: MyTripRequest & { client?: Client },
) => Promise<
  | { status: GetAccessTokenStatus.SUCCESS; token: string }
  | { status: GetAccessTokenStatus.RATE_LIMITED; rateLimit: RateLimit }
  | {
      status:
        | GetAccessTokenStatus.NAME_DOES_NOT_MATCH
        | GetAccessTokenStatus.BAD_BOOKING_CODE
        | GetAccessTokenStatus.BOOKING_NOT_FOUND
        | GetAccessTokenStatus.BOOKING_IN_THE_PAST
        | GetAccessTokenStatus.UNEXPECTED_STRING_RESPONSE
        | GetAccessTokenStatus.GENERAL_ERROR;
      token: null;
    }
>;

export const getAccessToken: GetAccessTokenHandler = async args => {
  const { client, ...body } = args;

  let { data, response, error } = await Account.accessToken({
    body,
    client,
  });

  let rateLimit = await createRateLimitHandler(
    ACCESS_TOKEN_RATE_LIMIT_NAME,
    response,
  );

  if (rateLimit.limited) {
    return {
      status: GetAccessTokenStatus.RATE_LIMITED,
      rateLimit,
      token: null,
    };
  }

  if (!error) {
    if (data?.token) {
      return {
        status: GetAccessTokenStatus.SUCCESS,
        token: data.token,
      };
    }

    return {
      status: GetAccessTokenStatus.GENERAL_ERROR,
      token: null,
    };
  }

  logger.error(
    `[Account] Failed to retrieve access token. ${JSON.stringify(args)}`,
  );
  logger.error(error);

  if (
    errorContains(error, 'bad request parameter - bad firstName or lastName')
  ) {
    return {
      status: GetAccessTokenStatus.NAME_DOES_NOT_MATCH,
      token: null,
    };
  }

  if (errorContains(error, 'bad request parameter - bad bookingCode')) {
    return {
      status: GetAccessTokenStatus.BAD_BOOKING_CODE,
      token: null,
    };
  }

  if (errorContains(error, 'booking in the past')) {
    return {
      status: GetAccessTokenStatus.BOOKING_IN_THE_PAST,
      token: null,
    };
  }

  if (errorContains(error, 'booking not found')) {
    return {
      status: GetAccessTokenStatus.BOOKING_NOT_FOUND,
      token: null,
    };
  }

  if (errorContains(error, 'name does not match')) {
    return {
      status: GetAccessTokenStatus.NAME_DOES_NOT_MATCH,
      token: null,
    };
  }

  if (errorContains(error, 'unexpected string response')) {
    return {
      status: GetAccessTokenStatus.UNEXPECTED_STRING_RESPONSE,
      token: null,
    };
  }

  return {
    status: GetAccessTokenStatus.GENERAL_ERROR,
    token: null,
  };
};

export function getTranslatedAccessTokenRequestStatus(
  t: TFunction,
  status: GetAccessTokenStatus,
): string {
  switch (status) {
    case GetAccessTokenStatus.SUCCESS:
      return '';

    case GetAccessTokenStatus.RATE_LIMITED:
      return t(
        'Aufgrund zu vieler Fehlversuche, haben wir die Eingabe aus Sicherheitsgründen gesperrt. Bei weiteren Fragen wenden Sie sich bitte an das Serviceteam.',
      );

    case GetAccessTokenStatus.NAME_DOES_NOT_MATCH:
      return t(
        'Der eingegebene Name stimmt nicht mit der Buchung überein. Bitte korrigieren Sie Ihre Eingaben und versuchen Sie es erneut.',
      );

    case GetAccessTokenStatus.BAD_BOOKING_CODE:
    case GetAccessTokenStatus.BOOKING_NOT_FOUND:
    case GetAccessTokenStatus.UNEXPECTED_STRING_RESPONSE:
      return t(
        'Die Vorgangsnummer ist ungültig. Bitte korrigieren Sie Ihre Eingaben und versuchen Sie es erneut.',
      );

    case GetAccessTokenStatus.BOOKING_IN_THE_PAST:
      return t('Ihre Reise mit uns ist bereits zu Ende gegangen.');

    case GetAccessTokenStatus.GENERAL_ERROR:
    default:
      return t(
        'Leider haben wir aktuell technische Probleme. Bitte versuchen Sie es in wenigen Minuten erneut. Bitte entschuldigen Sie die Unannehmlichkeiten!',
      );
  }
}
